"use client";

import React, { useState } from "react";
import { cn } from "utils";
import { Icon, ospThemes, OspToast } from "@ospweb/sdk-ui";
import type { SocialLoginProvider } from "@/src/features/auth/compat/sdk-types";
import log from "logbeacon";
import { LoginButtonConfig, LoginButtonsProps } from "./types";
import Translations from "@translate/components/LoginGuidePopup/LoginGuideContent/LoginButtons";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

export const LoginButtons: React.FC<LoginButtonsProps> = ({
  buttons,
  moreButtons,
  onClickLogin,
  className,
  termsAccepted,
  subLoginType,
  loading,
}) => {
  const [showMore, setShowMore] = useState(false);

  const handleLogin = (provider: SocialLoginProvider) => {
    if (!termsAccepted) {
      OspToast.error({
        content: I18n.t("Please agree to the Privacy Policy first"),
      });
      return;
    }
    try {
      onClickLogin(provider);
    } catch (error) {
      log.error("[LoginButtons] handleLogin error", error);
    }
  };

  const renderButton = ({
    provider,
    label,
    isPrimary,
    enabled = true,
    iconName,
  }: LoginButtonConfig) => {
    const isLoading = subLoginType === provider && loading;
    return (
      <button
        aria-label="Login Buttons"
        data-openreplay-label="Login Buttons Button"
        id="btn-login-buttons"
        key={provider}
        className={cn(
          "button sm_button min-w-[148px] group",
          isPrimary ? "primary_button" : "secondary_button",
        )}
        onClick={() => !loading && enabled && handleLogin(provider)}
        disabled={enabled ? loading : false}
        data-loading={isLoading}
      >
        <Icon
          name={iconName}
          size={20}
          className={cn("group-loading:hidden", "text-content_primary")}
        />
        <Icon
          name="ic_loading2"
          size={20}
          color={ospThemes.colors.content_secondary}
          className={cn("hidden animate-spin group-loading:block")}
        />
        <span
          className={cn("button_m text-content_primary group-loading:hidden", {
            "text-content_inverse_primary": isPrimary,
          })}
        >
          {label}
        </span>
      </button>
    );
  };

  const hasMore = moreButtons && moreButtons.length > 0;

  return (
    <div className={cn("flex flex-row gap-3 w-full flex-wrap", className)}>
      {buttons.map(renderButton)}
      {showMore && hasMore && moreButtons.map(renderButton)}
      {!showMore && hasMore && (
        <button
          type="button"
          onClick={() => setShowMore(true)}
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
            "border border-solid border-border_solid bg-transparent",
            "text-content_tertiary hover:text-content_secondary hover:border-border_solid_secondary transition-colors",
          )}
          aria-label="More login options"
        >
          <span className="flex gap-[3px]">
            <span className="size-[4px] rounded-full bg-current" />
            <span className="size-[4px] rounded-full bg-current" />
            <span className="size-[4px] rounded-full bg-current" />
          </span>
        </button>
      )}
    </div>
  );
};

LoginButtons.displayName = "LoginButtons";
